import { useReducer } from 'react';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { Pagination } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import AudiencesController from '@mm/state/controllers/AudiencesController';
import { AudiencesPaginationModel } from '@mm/state/model/AudiencePaginationModel';

type State = {
  busy: boolean;
  entities: AudiencesModel[];
  pagination?: Pagination;
  query?: string;
};

const defaults: State = {
  busy: false,
  entities: [],
  query: undefined,
  pagination: {
    page: 0,
    perPage: 10,
    total: 0,
    totalPages: 0,
    isLastPage: true,
  },
};

const actions = {
  busy: 'busy',
  search: 'search',
  searched: 'searched',
  retrieved: 'retrieved',
  reset: 'reset',
} as const;

type ActionsType = typeof actions;

type Action<T> = { type: ActionsType[keyof ActionsType]; payload?: T };

const reducer = (
  state: State,
  action: Action<AudiencesPaginationModel | string | undefined>,
) => {
  const { type, payload } = action;

  const { audiences: entities, pagination } =
    (payload as AudiencesPaginationModel) || {};

  switch (type) {
    case actions.busy:
      return { ...state, busy: true };
    case actions.search:
      return { ...state, query: payload };
    case actions.searched:
      return {
        ...state,
        entities,
        pagination,
        busy: false,
      };
    case actions.retrieved:
      return {
        ...state,
        entities: [...state.entities, ...entities],
        pagination,
        busy: false,
      };
    case actions.reset:
      return defaults;
    default:
      return state;
  }
};

export const useAudiencesReducer = () => {
  const [state, dispatch] = useReducer(reducer, defaults);
  const { pagination, query } = state;
  const { page } = pagination as Pagination;

  const botId = useSelector(activeChatbotIdSelector);

  const retrieve = async () => {
    dispatch({ type: actions.busy });

    const res = await AudiencesController.getAll(
      botId,
      page + 1,
      20,
      query as string,
    );
    dispatch({ type: actions.retrieved, payload: res });
  };

  const search = async (query?: string) => {
    dispatch({ type: actions.busy });

    // first time or cleared?
    const nextPage = !query || !state.query ? 1 : page + 1;

    dispatch({ type: actions.search, payload: query });
    const res = await AudiencesController.getAll(botId, nextPage, 70, query);

    dispatch({ type: actions.searched, payload: res });
  };

  const reset = () => {
    dispatch({ type: actions.reset });
  };

  return { state, dispatcher: { retrieve, search, reset } };
};
